import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import currentTimeZone from '../../../../../config/timeZone';
import { DateStatusProperties } from '../../../../../interface/timeline.interface';
import { useAppDispatch, useAppSelector } from '../../../../../redux/hooks';
import { setSelectedDevice } from '../../../../../redux/slices/DeviceManagementSlice';
import CustomRadioButton from '../../../../../shared-components/custom-radio-button/CustomRadioButton';
import HeatMapCalendar from '../../../../../shared-components/heatmap-calendar/HeatMapCalendar';
import {
  interpolateColorThreeStops,
  reduceColorOpacity,
} from '../../../../../shared-functions/ColorFunctions';
import getCurrentTimeInLocalISO from '../../../../../shared-functions/CurrentTimeInLocalISO';
import FormatDuration from '../../../../../shared-functions/FormatDuration';
import device from '../../../../../utils/api/device';
import deviceHubApi from '../../../../../utils/api/device-hub';

import ConnectivityTableView from './timeline/ConnectivityTableView';
import ConnectivityTimelineView from './timeline/ConnectivityTimelineView';
import ConsumptionTableView from './timeline/ConsumptionTableView';
import ConsumptionTimelineView from './timeline/ConsumptionTimelineView';

export type { DateStatusProperties };

interface ConnectorStatusProperties {
  _id?: string;
  chargingPointSn?: string;
  connector?: number | string;
  status: 'Preparing' | 'Charging' | 'Finishing' | 'Available' | 'Faulted' | 'Unavailable' | string;
  startsAt: string; // ISO date string
  endsAt: string | null; // ISO date string or null
  startsAtAdjusted?: string;
  endsAtAdjusted?: string | null;
}

interface DeviceStatusProperties {
  _id: string;
  chargingPointSn: string;
  connector: number;
  status: 'Preparing' | 'Charging' | 'Finishing' | string; // Add other statuses if any
  startsAt: string; // ISO date string
  endsAt: string; // ISO date string
  startsAtAdjusted: string;
  endsAtAdjusted: string;
  transactionStartsAtAdjusted: string;
  transactionEndsAtAdjusted: string;
  // PowerConsumption Data
  meterStart?: number; // Optional, if not available
  meterStop?: number; // Optional, if not available
  powerConsumed?: number; // Optional, if not available
  energyDelivered?: number; // Optional
  totalRevenue?: number; // Optional
  totalDuration?: number; // Optional
  sessionCount?: number; // Optional
  cpoRevenueShare?: number; // Optional
  partnerRevenueShare?: number; // Optional
  // Connectivity
  onlineHours?: number; // Optional, if not available
  offlineHours?: number; // Optional, if not available
}

interface ChargingPointConnectorStatusProperties {
  _id: number;
  connectorStatus: ConnectorStatusProperties[];
}

/**
 * Format date in local time as YYYY-MM-DD
 * @param {Date} date - The date
 * @returns {string} Formatted date string
 */
const DateFormatter = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * Function to process device status data and calc active hours effectively
 * @param {number} month - The month
 * @param {number} year - The year
 * @param {any} rawData - The rawData
 * @returns {any} processed daily data
 */
const processDeviceStatusRawData = (
  month: number,
  year: number,
  rawData: any,
) => {
  const daysInMonth = new Date(year, month, 0).getDate();
  const now = new Date();
  const isCurrentMonth = now.getFullYear() === year && now.getMonth() + 1 === month;
  const lastDay = isCurrentMonth ? now.getDate() : daysInMonth;

  // Initialize result object for each day in the month
  const result: Record<string, any> = {};
  for (let day = 1; day <= lastDay; day += 1) {
    const dateString = `${year}-${month.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`;
    result[dateString] = {
      date: dateString,
      onlineHours: 0,
      offlineHours: 0,
      powerConsumed: 0,
      energyDelivered: 0,
      totalRevenue: 0,
      totalDuration: 0,
      sessionCount: 0,
      cpoRevenueShare: 0,
      partnerRevenueShare: 0,
      uptime: '0.00%',
    };
  }

  // Check if response has a "days" array or list of day items
  const rawDays =
    rawData?.days ||
    (Array.isArray(rawData?.data?.days) ? rawData.data.days : null) ||
    (Array.isArray(rawData?.data) && rawData.data[0]?.date ? rawData.data : null) ||
    (Array.isArray(rawData) && rawData[0]?.date ? rawData : null);

  if (Array.isArray(rawDays)) {
    rawDays.forEach((day: any) => {
      if (day && day.date) {
        let onlineHours = 0;
        if (day.onlineHours !== undefined) {
          onlineHours = Number(day.onlineHours);
        } else if (day.onlineSeconds !== undefined) {
          onlineHours = Math.round((Number(day.onlineSeconds) / 3600) * 100) / 100;
        } else if (day.totalDuration !== undefined) {
          onlineHours = Math.round((Number(day.totalDuration) / 60) * 100) / 100;
        } else if (day.activeHours !== undefined) {
          onlineHours = Number(day.activeHours);
        }

        let offlineHours = 0;
        if (day.offlineHours !== undefined) {
          offlineHours = Number(day.offlineHours);
        } else if (day.offlineSeconds !== undefined) {
          offlineHours = Math.round((Number(day.offlineSeconds) / 3600) * 100) / 100;
        } else if (day.idleHours !== undefined) {
          offlineHours = Number(day.idleHours);
        } else if (day.totalDuration !== undefined) {
          offlineHours = Math.max(0, Math.round((24 - onlineHours) * 100) / 100);
        }

        const powerConsumed = Number(
          day.energyDelivered ??
            day.powerConsumed ??
            day.totalPowerConsumed ??
            day.totalEnergyDelivered ??
            0,
        );

        let uptime = '0.00%';
        if (day.uptimePercent !== undefined) {
          uptime = `${Number(day.uptimePercent).toFixed(2)}%`;
        } else if (day.uptime !== undefined) {
          uptime =
            typeof day.uptime === 'string' && day.uptime.endsWith('%')
              ? day.uptime
              : `${Number(day.uptime).toFixed(2)}%`;
        } else {
          const total = onlineHours + offlineHours;
          if (total > 0) {
            uptime = `${((onlineHours / total) * 100).toFixed(2)}%`;
          }
        }

        result[day.date] = {
          date: day.date,
          onlineHours,
          offlineHours,
          powerConsumed,
          energyDelivered: powerConsumed,
          totalRevenue: Number(day.totalRevenue ?? 0),
          totalDuration: Number(day.totalDuration ?? 0),
          sessionCount: Number(day.sessionCount ?? 0),
          cpoRevenueShare: Number(day.cpoRevenueShare ?? 0),
          partnerRevenueShare: Number(day.partnerRevenueShare ?? 0),
          uptime,
        };
      }
    });

    return Object.values(result);
  }

  // Filter data for the specified month and year, and handle null endsAtAdjusted
  let safeRawData: any[] = [];
  if (Array.isArray(rawData)) {
    safeRawData = rawData;
  } else if (Array.isArray(rawData?.data)) {
    safeRawData = rawData.data;
  }
  const today = new Date(now);
  today.setHours(0, 0, 0, 0);

  const todayEnd = new Date(today);
  todayEnd.setHours(23, 59, 59, 999);

  const processedData = safeRawData.map((item: any) => {
    let startDate = new Date(item.startsAtAdjusted || item.startsAt);
    let endDate = item.endsAtAdjusted || item.endsAt
      ? new Date(item.endsAtAdjusted || item.endsAt)
      : new Date(getCurrentTimeInLocalISO());

    // Clamp to today’s end if the date is in the future
    const usedFallback = !(item.endsAtAdjusted || item.endsAt);

    if (isCurrentMonth && (usedFallback || endDate > todayEnd)) {
      endDate = todayEnd;
    }

    // Clamp start date if before the month
    const monthStartAt = new Date(year, month - 1, 1, 0, 0, 0, 0);
    if (startDate < monthStartAt) {
      startDate = monthStartAt;
    }

    return {
      ...item,
      startsAtAdjusted: startDate,
      endsAtAdjusted: endDate,
    };
  });

  // Sort by start time
  processedData.sort(
    (a: any, b: any) => a.startsAtAdjusted.getTime() - b.startsAtAdjusted.getTime(),
  );

  // Get the first and last moment of the target month
  const monthStart = new Date(year, month - 1, 1);
  const monthEnd = new Date(year, month, 0, 23, 59, 59, 999);

  // Process each status period
  /* eslint-disable no-restricted-syntax */
  for (const item of processedData) {
    // Here each item contains meterStart and meterStop
    let start = item?.startsAtAdjusted;
    let end = item?.endsAtAdjusted;
    const status = item?.status;
    const meterStart = item?.meterStart || 0;
    const meterStop = item?.meterStop || 0;
    const powerDiff = (meterStop - meterStart) / 1000;

    // Adjust start and end to be within the target month
    start = start < monthStart ? monthStart : start;
    end = end > monthEnd ? monthEnd : end;

    // Process each day in the period
    const currentDay = new Date(start);
    currentDay.setHours(0, 0, 0, 0);
    while (currentDay <= end) {
      const dayStart = new Date(currentDay);
      const dayEnd = new Date(currentDay);
      dayEnd.setDate(dayEnd.getDate() + 1);

      // Calculate overlap between status period and this day
      const overlapStart = new Date(Math.max(start.getTime(), dayStart.getTime()));
      const overlapEnd = new Date(Math.min(end.getTime(), dayEnd.getTime()));

      const hours = (overlapEnd.getTime() - overlapStart.getTime()) / (1000 * 60 * 60);

      const dateString = `${year}-${month.toString().padStart(2, '0')}-${currentDay.getDate().toString().padStart(2, '0')}`;

      if (result[dateString]) {
        if (status === 'online' || status === 'Charging') {
          result[dateString].onlineHours = (result[dateString]?.onlineHours ?? 0) + hours;
        } else {
          result[dateString].offlineHours = (result[dateString]?.offlineHours ?? 0) + hours;
        }

        // Add power consumption only on the day when the period ends
        if (overlapEnd.getTime() === end.getTime() && currentDay.getDate() === end.getDate()) {
          result[dateString].powerConsumed = (result[dateString]?.powerConsumed ?? 0) + powerDiff;
          result[dateString].energyDelivered = result[dateString].powerConsumed;
        }
      }

      currentDay.setDate(currentDay.getDate() + 1);
    }
  }

  // Convert to array and round hours to 2 decimal places
  return Object.values(result).map((day: any) => ({
    ...day,
    onlineHours: Math.round(day.onlineHours * 100) / 100,
    offlineHours: Math.round(day.offlineHours * 100) / 100,
  }));
};

/**
 * Fills gaps between charging transactions with "Not Charging" periods.
 * @param {any} transactions - Array of charging transactions
 * @param {any} startTimeISO - ISO date string (start of the range, e.g., "2025-07-31T00:00:00Z")
 * @param {any} endTimeISO - ISO date string (end of the range, e.g., current time)
 * @returns {any} Array of transactions with gaps filled
 */
function fillDeviceConsumptionDailyGaps(
  transactions: DeviceStatusProperties[],
  startTimeISO: string,
  endTimeISO: string,
) {
  const safeTransactions = Array.isArray(transactions) ? transactions : [];
  const sorted = [...safeTransactions].sort(
    (a, b) =>
      new Date(a.transactionStartsAtAdjusted).getTime() -
      new Date(b.transactionStartsAtAdjusted).getTime(),
  );

  const filled: any = [];
  let pointer = new Date(startTimeISO);
  const endTime = new Date(endTimeISO);

  for (const txn of sorted) {
    const txnStart = new Date(txn.transactionStartsAtAdjusted);
    const txnEnd = new Date(txn.transactionEndsAtAdjusted);

    if (txnStart > pointer) {
      filled.push({
        _id: txn._id,
        transactionStartsAtAdjusted: pointer.toISOString(),
        transactionEndsAtAdjusted: txnStart.toISOString(),
        status: 'idle',
        chargingPointSn: txn?.chargingPointSn || '',
        connector: txn?.connector || 1,
      });
    }

    filled.push(txn);
    pointer = txnEnd > pointer ? txnEnd : pointer;
  }

  if (pointer < endTime) {
    filled.push({
      _id: 'Final Gap',
      transactionStartsAtAdjusted: pointer.toISOString(),
      transactionEndsAtAdjusted: endTime.toISOString(),
      status: 'idle',
    });
  }

  return filled;
}

/**
 * Device management timeline view
 * @returns {any} The result
 */
function Timeline() {
  const { t, i18n } = useTranslation('devices');
  const dispatch = useAppDispatch();
  const [selectedDate, setSelectedDate] = useState(new Date());
  const { selectedChargingPoint } = useAppSelector((s) => s.device);
  const [dateStatus, setDateStatus] = useState<DateStatusProperties[]>([]);
  const [currentTab, setCurrentTab] = useState('consumption');
  const [timelineOrTableView, setTableOrTableView] = useState('timeline');

  const [timelineDataLoading, setTimelineDataLoading] = useState({
    consumption: false,
    connectivity: false,
    connectorStatus: false,
    heatMap: false,
  });

  const [heatMapMinMaxValues, setHeatMapMinMaxValues] = useState({
    minimum: 0,
    maximum: 0,
  });

  const [consumptionTimelineData, setConsumptionTimelineData] = useState<DeviceStatusProperties[]>(
    [],
  );
  const [connectivityTimelineData, setConnectivityTimelineData] = useState<DeviceStatusProperties[]>(
    [],
  );
  const [connectorStatus, setConnectorStatus] = useState<ChargingPointConnectorStatusProperties[]>(
    [],
  );

  const [monthAndYear, setMonthAndYear] = useState({
    month: selectedDate?.getMonth()?.toString(), // Using the already defined currentMonth
    year: selectedDate?.getFullYear()?.toString(), // Using the already defined currentYear
  });

  const { id } = useParams<{ id: string }>();
  const activeId = id || selectedChargingPoint?.id;
  const [connectorCount, setConnectorCount] = useState<number>(0);

  useEffect(() => {
    /**
     * Fetch device general details to get connector count
     */
    const fetchDeviceDetails = async () => {
      if (activeId) {
        try {
          const isDeviceHub = window.location.pathname.includes('ev-device-hub');
          if (isDeviceHub) {
            const response = await deviceHubApi.fetchAdvanceDeviceDetails(activeId);
            const data = response?.data || response || {};
            const connectorsList = data.connectorInformation || [];
            setConnectorCount(connectorsList.length);
            if (!selectedChargingPoint?.chargingPointSn && (data.serialNumber || activeId)) {
              dispatch(
                setSelectedDevice({
                  id: data._id || data.id || activeId,
                  name: data.serialNumber || '',
                  chargingPointSn: data.serialNumber || '',
                  modelSeries: data.modelSeries || '',
                }),
              );
            }
          } else {
            const response = await device.fetchChargingPointGeneralDetails(activeId);
            const connectorsList = response?.data?.connectors || [];
            setConnectorCount(connectorsList.length);
            const enelData = response?.data?.enelData || {};
            const sn =
              response?.data?.chargingPointSn ||
              response?.data?.serialNumber ||
              (enelData.sn !== undefined ? String(enelData.sn) : '');
            if (!selectedChargingPoint?.chargingPointSn && sn) {
              dispatch(
                setSelectedDevice({
                  id: response?.data?._id || response?.data?.id || activeId,
                  name: response?.data?.chargingPointName || response?.data?.chargingPointSn || '',
                  chargingPointSn: sn,
                  modelSeries: response?.data?.modelSeries || '',
                }),
              );
            }
          }
        } catch (error) {
          console.log('Error fetching device general details in Timeline:', error);
        }
      }
    };
    fetchDeviceDetails();
  }, [activeId, selectedChargingPoint?.chargingPointSn]);

  /**
   * Function to update gradient colors for device status data
   * @param {any} data - The data
   * @param {any} type - The type
   * @returns {any} The result
   */
  function assignGradientColors(
    data: DeviceStatusProperties[],
    type: 'connectivity' | 'consumption',
  ) {
    const safeData = Array.isArray(data) ? data : [];
    let values: number[] = [];
    if (type === 'consumption') {
      values = safeData.map((item) => Number(item?.powerConsumed || 0));
    }

    if (type === 'connectivity') {
      values = safeData.map((item) => Number(item?.onlineHours || 0));
    }

    let min = 0;
    if (type === 'consumption') {
      min = values.length > 0 ? Math.min(...values) : 0;
    }

    const max = values.length > 0 ? Math.max(...values) : 0;
    const range = max - min || 1; // Prevent division by zero

    // Update the min & max values to the state
    setHeatMapMinMaxValues({
      minimum: Number.isFinite(min) ? min : 0,
      maximum: Number.isFinite(max) ? max : 0,
    });

    return safeData.map((item) => {
      const value =
        type === 'consumption' ? Number(item?.powerConsumed || 0) : Number(item?.onlineHours || 0);
      const normalized = Math.max(0, Math.min(1, (value - min) / range || 0));

      // Custom color code
      const color = interpolateColorThreeStops('#FF0101', '#FF5A0F', '#2E9E3A', normalized);

      return {
        ...item,
        color,
      };
    });
  }

  /**
   * Fetch calendar heatMap data month wise
   * @param {number} month - The month
   * @param {number} year - The year
   */
  const fetchConsumptionMonthWise = async (month: number, year: number) => {
    setTimelineDataLoading((previous) => ({
      ...previous,
      heatMap: true,
    }));
    const sn = selectedChargingPoint?.chargingPointSn || selectedChargingPoint?.id || activeId;
    if (sn) {
      try {
        const response = await device.fetchChargingPointConceptionMonthWise(sn, {
          month,
          year,
          timeZone: currentTimeZone,
        });
        const rawData = response?.data || response || [];
        const processedData = processDeviceStatusRawData(month, year, rawData);
        const colorAssignedData = assignGradientColors(processedData, 'consumption');

        const consumptionValues = processedData.map((item) => Number(item?.powerConsumed || 0));
        const maxConsumption = consumptionValues.length > 0 ? Math.max(...consumptionValues) : 0;

        setDateStatus(
          colorAssignedData?.map((eachDate: any, index: number) => {
            const online = Number(eachDate?.onlineHours || 0);
            const offline = Number(eachDate?.offlineHours || 0);
            const total = online + offline;
            const uptime =
              eachDate?.uptime ||
              (total > 0 ? `${((online / total) * 100).toFixed(2)}%` : '0.00%');
            const power = Number(eachDate?.powerConsumed || 0);
            const hasConsumption = power > 0;
            const ratio = maxConsumption > 0 ? power / maxConsumption : 0;

            let tier: 'zero' | 'low' | 'medium' | 'high' = 'zero';
            let backgroundColor = 'var(--pure-white-color)';
            let borderColor = 'var(--border-grey)';
            let textColor = 'var(--text-color)';
            let labelColor = 'var(--grey-1)';

            if (hasConsumption) {
              if (ratio <= 0.35) {
                tier = 'low';
                backgroundColor = 'var(--error-bg)';
                borderColor = 'var(--error)';
                textColor = '#991B1B';
                labelColor = 'var(--error)';
              } else if (ratio <= 0.7) {
                tier = 'medium';
                backgroundColor = 'var(--warning-bg)';
                borderColor = 'var(--warning)';
                textColor = '#92400E';
                labelColor = 'var(--warning)';
              } else {
                tier = 'high';
                backgroundColor = 'var(--success-bg)';
                borderColor = 'var(--success)';
                textColor = '#14532D';
                labelColor = 'var(--success)';
              }
            }

            return {
              ...eachDate,
              id: eachDate?.date || String(index),
              dateToShow: eachDate?.date,
              date: Number(eachDate?.date?.split('-')[2]),
              tier,
              backgroundColor,
              borderColor,
              textColor,
              labelColor,
              powerConsumed: hasConsumption
                ? `${power.toFixed(3)} kW`
                : '0 kW',
              uptime,
            };
          }),
        );
      } catch (error) {
        console.log('Error fetching consumption month wise:', error);
      }
    }
    setTimelineDataLoading((previous) => ({
      ...previous,
      heatMap: false,
    }));
  };

  /**
   * Function to fetch timeline
   * @param {Date} date - The date
   */
  const fetchConsumptionData = async (date: Date) => {
    setTimelineDataLoading((previous) => ({
      ...previous,
      consumption: true,
    }));

    const isFutureDate = date > new Date(); // Compare selected date with current date

    if (isFutureDate) {
      setConsumptionTimelineData([]); // Set empty array for future dates
      setTimelineDataLoading((previous) => ({
        ...previous,
        consumption: false,
      }));
      return;
    }

    const sn = selectedChargingPoint?.chargingPointSn || selectedChargingPoint?.id || activeId;
    if (sn) {
      try {
        const response = await device.fetchChargingPointConceptionByDate(sn, {
          date: DateFormatter(date),
          timeZone: currentTimeZone,
        });

        const rawData = response?.data || response;
        let consumptionArray: any[] = [];
        if (rawData?.timeline && Array.isArray(rawData.timeline)) {
          consumptionArray = rawData.timeline.map((item: any, index: number) => ({
            _id: item._id || `consumption-${index}`,
            chargingPointSn: rawData.chargingPointSn || sn,
            connector: 1,
            status: item.status || item.state || 'idle',
            startsAt: item.startsAt,
            endsAt: item.endsAt,
            startsAtAdjusted: item.startsAtAdjusted || item.startsAt,
            endsAtAdjusted: item.endsAtAdjusted || item.endsAt,
            transactionStartsAtAdjusted: item.transactionStartsAtAdjusted || item.startsAt,
            transactionEndsAtAdjusted: item.transactionEndsAtAdjusted || item.endsAt,
          }));
          setConsumptionTimelineData(consumptionArray);
        } else if (rawData?.statusTimeline && Array.isArray(rawData.statusTimeline)) {
          consumptionArray = rawData.statusTimeline.map((item: any, index: number) => ({
            _id: item._id || `consumption-${index}`,
            chargingPointSn: rawData.chargingPointSn || sn,
            connector: 1,
            status: item.status || item.state || 'idle',
            startsAt: item.startsAt,
            endsAt: item.endsAt,
            startsAtAdjusted: item.startsAtAdjusted || item.startsAt,
            endsAtAdjusted: item.endsAtAdjusted || item.endsAt,
            transactionStartsAtAdjusted: item.transactionStartsAtAdjusted || item.startsAt,
            transactionEndsAtAdjusted: item.transactionEndsAtAdjusted || item.endsAt,
          }));
          setConsumptionTimelineData(consumptionArray);
        } else if (Array.isArray(rawData)) {
          const isToday = DateFormatter(new Date()) === DateFormatter(date);
          const filledData =
            fillDeviceConsumptionDailyGaps(
              rawData,
              `${DateFormatter(date)}T00:00:00Z`,
              isToday ? getCurrentTimeInLocalISO() : `${DateFormatter(date)}T23:59:59.000Z`,
            ) || [];
          setConsumptionTimelineData(Array.isArray(filledData) ? filledData : []);
        } else if (Array.isArray(rawData?.data)) {
          const isToday = DateFormatter(new Date()) === DateFormatter(date);
          const filledData =
            fillDeviceConsumptionDailyGaps(
              rawData.data,
              `${DateFormatter(date)}T00:00:00Z`,
              isToday ? getCurrentTimeInLocalISO() : `${DateFormatter(date)}T23:59:59.000Z`,
            ) || [];
          setConsumptionTimelineData(Array.isArray(filledData) ? filledData : []);
        } else {
          setConsumptionTimelineData([]);
        }
      } catch (error) {
        console.log('Error fetching consumption data:', error);
        setConsumptionTimelineData([]);
      }
    } else {
      setConsumptionTimelineData([]);
    }

    // Reset loading state after fetching data
    setTimelineDataLoading((previous) => ({
      ...previous,
      consumption: false,
    }));
  };

  /**
   * Fetch connector status by when the selected date is changed
   * @param {Date} date - The date
   */
  const fetchConnectorStatusData = async (date: Date) => {
    setTimelineDataLoading((previous) => ({
      ...previous,
      connectorStatus: true,
    }));

    const isFutureDate = date > new Date(); // Compare selected date with current date

    if (isFutureDate) {
      setConnectorStatus([]);
      setTimelineDataLoading((previous) => ({
        ...previous,
        connectorStatus: false,
      }));
      return;
    }

    const sn = selectedChargingPoint?.chargingPointSn || selectedChargingPoint?.id || activeId;
    if (sn) {
      try {
        const response = await device.fetchConnectorStatusByDate(sn, {
          date: DateFormatter(date),
          timeZone: currentTimeZone,
          chargingPointId: sn,
        });

        const rawData = response?.data || response;
        let formattedConnectors: ChargingPointConnectorStatusProperties[] = [];

        if (rawData?.connectors && Array.isArray(rawData.connectors)) {
          formattedConnectors = rawData.connectors.map((c: any) => {
            const connectorNumber = Number(c.connector);
            return {
              _id: connectorNumber,
              connectorStatus: (c.statusTimeline || []).map((st: any) => ({
                _id: st._id || `${connectorNumber}-${st.startsAt}`,
                chargingPointSn: rawData.chargingPointSn || sn,
                connector: connectorNumber,
                status: st.status,
                startsAt: st.startsAt,
                endsAt: st.endsAt,
                startsAtAdjusted: st.startsAt,
                endsAtAdjusted: st.endsAt,
              })),
            };
          });
        } else if (Array.isArray(rawData)) {
          formattedConnectors = rawData;
        }

        setConnectorStatus(formattedConnectors);
      } catch (error) {
        console.log('Error fetching connector status data:', error);
        setConnectorStatus([]);
      }
    }

    setTimelineDataLoading((previous) => ({
      ...previous,
      connectorStatus: false,
    }));
  };

  /**
   * Fetch connector status by when the selected date is changed
   * @param {Date} date - The date
   */
  const fetchDeviceStatusData = async (date: Date) => {
    setTimelineDataLoading((previous) => ({
      ...previous,
      connectivity: true,
    }));
    const sn = selectedChargingPoint?.chargingPointSn || selectedChargingPoint?.id || activeId;
    if (sn) {
      try {
        const response = await device.fetchDeviceStatusByDate(sn, {
          date: DateFormatter(date),
          timeZone: currentTimeZone,
          chargingPointId: sn,
        });
        const rawData = response?.data || response;
        let statusArray: any[] = [];
        if (rawData?.statusTimeline && Array.isArray(rawData.statusTimeline)) {
          statusArray = rawData.statusTimeline.map((item: any) => ({
            ...item,
            startsAtAdjusted: item.startsAtAdjusted || item.startsAt,
            endsAtAdjusted: item.endsAtAdjusted || item.endsAt,
          }));
        } else if (Array.isArray(rawData)) {
          statusArray = rawData;
        } else if (Array.isArray(rawData?.data)) {
          statusArray = rawData.data;
        }
        setConnectivityTimelineData(statusArray);
      } catch (error) {
        console.log('Error fetching device status data:', error);
        setConnectivityTimelineData([]);
      }
    } else {
      setConnectivityTimelineData([]);
    }
    setTimelineDataLoading((previous) => ({
      ...previous,
      connectivity: false,
    }));
  };

  /**
   * Fetch calendar heatMap data month wise
   * @param {number} month - The month
   * @param {number} year - The year
   */
  const fetchDeviceStatusMonthWise = async (month: number, year: number) => {
    setTimelineDataLoading((previous) => ({
      ...previous,
      heatMap: true,
    }));
    const sn = selectedChargingPoint?.chargingPointSn || selectedChargingPoint?.id || activeId;
    if (sn) {
      try {
        const response = await device.fetchDeviceStatusMonthWise(sn, {
          month,
          year,
          timeZone: currentTimeZone,
        });
        const rawData = response?.data || response || [];
        const processedData = processDeviceStatusRawData(month, year, rawData);
        const colorAssignedData = assignGradientColors(processedData, 'connectivity');

        setDateStatus(
          colorAssignedData?.map((eachDate: any, index: number) => {
            const online = Number(eachDate?.onlineHours || 0);
            const offline = Number(eachDate?.offlineHours || 0);
            const total = online + offline;
            const uptime =
              eachDate?.uptime ||
              (total > 0 ? `${((online / total) * 100).toFixed(2)}%` : '0.00%');
            const hasOnline = online > 0;
            return {
              ...eachDate,
              id: eachDate?.date || String(index),
              dateToShow: eachDate?.date,
              date: Number(eachDate?.date?.split('-')[2]),
              backgroundColor:
                hasOnline && eachDate?.color
                  ? reduceColorOpacity(eachDate?.color, 0.4)
                  : 'var(--pure-white-color)',
              borderColor: hasOnline && eachDate?.color ? eachDate.color : 'var(--border-grey)',
              textColor: 'var(--text-color)',
              labelColor: hasOnline ? 'var(--text-color)' : 'var(--grey-1)',
              onlineHours: FormatDuration(eachDate?.onlineHours || 0, {
                defaultValue: '00h 00m 00s',
                formatBy: 'hr',
              }),
              uptime,
            };
          }),
        );
      } catch (error) {
        console.log(error);
      }
    }
    setTimelineDataLoading((previous) => ({
      ...previous,
      heatMap: false,
    }));
  };

  /**
   * handle change radio button function
   * @param {any} name - The name
   * @param {any} value - The value
   */
  const handleAvailabilityChange = (name: string, value: string) => {
    setCurrentTab(value);
  };

  /**
   * Function to fetch month wise data based on current tab and month/year selection
   */
  const fetchMonthWiseData = () => {
    if (currentTab === 'connectivity') {
      fetchDeviceStatusMonthWise(Number(monthAndYear.month) + 1, Number(monthAndYear.year));
    } else {
      fetchConsumptionMonthWise(Number(monthAndYear.month) + 1, Number(monthAndYear.year));
    }
  };

  /**
   * Function to fetch day wise data based on selected date and current tab
   */
  const fetchDayWiseData = () => {
    if (timelineOrTableView !== 'timeline') {
      return;
    }

    fetchConnectorStatusData(selectedDate);
    if (currentTab === 'connectivity') {
      fetchDeviceStatusData(selectedDate);
    } else {
      fetchConsumptionData(selectedDate);
    }
  };

  useEffect(() => {
    // Trigger fetch when `currentTab`, `chargingPointSn`, `activeId`, or month/year changes
    fetchMonthWiseData();
  }, [currentTab, selectedChargingPoint?.chargingPointSn, activeId, monthAndYear.month, monthAndYear.year]);

  useEffect(() => {
    // Trigger fetch when date, tab, timeline/table view mode, chargingPointSn, or activeId changes
    fetchDayWiseData();
  }, [selectedDate, currentTab, timelineOrTableView, selectedChargingPoint?.chargingPointSn, activeId]);

  const maxConnectorIdFromStatus =
    connectorStatus?.reduce((max, item) => (item._id > max ? item._id : max), 0) || 0;
  const totalPhysicalConnectors = Math.max(connectorCount, maxConnectorIdFromStatus);

  const connectorListToRender: ChargingPointConnectorStatusProperties[] = [];
  const renderedIds = new Set<number>();

  // If connector 0 is present in the API status response, add it as Device Status
  const connector0Status = connectorStatus?.find((c) => c._id === 0);
  if (connector0Status) {
    connectorListToRender.push(connector0Status);
    renderedIds.add(0);
  }

  // Add physical connectors 1..totalPhysicalConnectors
  for (let connectorIndex = 1; connectorIndex <= totalPhysicalConnectors; connectorIndex += 1) {
    const existingStatus = connectorStatus?.find((c) => c._id === connectorIndex);
    connectorListToRender.push({
      _id: connectorIndex,
      connectorStatus: existingStatus ? existingStatus.connectorStatus : [],
    });
    renderedIds.add(connectorIndex);
  }

  // Any other connector IDs returned that were not included yet
  connectorStatus?.forEach((c) => {
    if (!renderedIds.has(c._id)) {
      connectorListToRender.push(c);
      renderedIds.add(c._id);
    }
  });

  return (
    <div className="device-management-timeline-wrap" dir={i18n.dir()}>
      <div className="device-timeline-table-view-container">
        <div className="device-timeline-table-view-toggle-container">
          <CustomRadioButton
            name=""
            value="timeline"
            label={t('timeline')}
            selected={timelineOrTableView === 'timeline'}
            onChangeFunc={() => setTableOrTableView('timeline')}
            fontSize="small"
          />
          <CustomRadioButton
            name=""
            value="table"
            label={t('table')}
            selected={timelineOrTableView === 'table'}
            onChangeFunc={() => setTableOrTableView('table')}
            fontSize="small"
          />
        </div>
        {/* 1. Consumption Timeline Chart View */}
        {currentTab === 'consumption' && timelineOrTableView === 'timeline' && (
          <ConsumptionTimelineView
            topTimelineData={consumptionTimelineData}
            connectorListToRender={connectorListToRender}
            selectedDateString={DateFormatter(selectedDate)}
            dataLoading={timelineDataLoading?.consumption}
            connectorLoading={timelineDataLoading?.connectorStatus}
          />
        )}

        {/* 2. Consumption Table View */}
        {currentTab === 'consumption' && timelineOrTableView === 'table' && (
          <ConsumptionTableView
            data={dateStatus}
            loading={timelineDataLoading?.heatMap}
          />
        )}

        {/* 3. Connectivity Timeline Chart View */}
        {currentTab === 'connectivity' && timelineOrTableView === 'timeline' && (
          <ConnectivityTimelineView
            topTimelineData={connectivityTimelineData}
            connectorListToRender={connectorListToRender}
            selectedDateString={DateFormatter(selectedDate)}
            dataLoading={timelineDataLoading?.connectivity}
            connectorLoading={timelineDataLoading?.connectorStatus}
          />
        )}

        {/* 4. Connectivity Table View */}
        {currentTab === 'connectivity' && timelineOrTableView === 'table' && (
          <ConnectivityTableView
            data={dateStatus}
            loading={timelineDataLoading?.heatMap}
          />
        )}
      </div>
      <div className="device-calendar-view-container">
        <div className="device-calendar-view-header">
          <div className="device-calendar-view-toggle-container">
            <CustomRadioButton
              name=""
              value="consumption"
              label={t('consumption')}
              selected={currentTab === 'consumption'}
              onChangeFunc={handleAvailabilityChange}
              fontSize="small"
            />
            <CustomRadioButton
              name=""
              value="connectivity"
              label={t('connectivity')}
              selected={currentTab === 'connectivity'}
              onChangeFunc={handleAvailabilityChange}
              fontSize="small"
            />
          </div>
          {currentTab === 'consumption' && (
            <div className="calendar-min-max-warp">
              <span className="calendar-value-min">
                {t('min')}: {Number(heatMapMinMaxValues?.minimum || 0).toFixed(3)} kW
              </span>
              <span className="calendar-value-max">
                {t('max')}: {Number(heatMapMinMaxValues?.maximum || 0).toFixed(3)} kW
              </span>
            </div>
          )}
          {currentTab === 'connectivity' && (
            <div className="calendar-min-max-warp">
              <span className="calendar-value-min">
                {t('min')}:{' '}
                {FormatDuration(heatMapMinMaxValues?.minimum || 0, {
                  defaultValue: '00h 00m 00s',
                  formatBy: 'hr',
                })}
              </span>
              <span className="calendar-value-max">
                {t('max')}:{' '}
                {FormatDuration(heatMapMinMaxValues?.maximum || 0, {
                  defaultValue: '00h 00m 00s',
                  formatBy: 'hr',
                })}
              </span>
            </div>
          )}
        </div>
        <HeatMapCalendar
          dateStatusData={dateStatus}
          selectedDate={selectedDate}
          setSelectedDate={setSelectedDate}
          // handleMonthOrYearChange={fetchConsumptionMonthWise}
          handleMonthOrYearChange={
            currentTab === 'connectivity' ? fetchDeviceStatusMonthWise : fetchConsumptionMonthWise
          }
          monthAndYear={monthAndYear}
          setMonthAndYear={setMonthAndYear}
          keyToLookupUnderData={currentTab === 'consumption' ? 'powerConsumed' : 'onlineHours'}
          defaultValueToShow={currentTab === 'consumption' ? '0 kW' : '00h 00m 00s'}
          dataLoading={timelineDataLoading?.heatMap}
        />
      </div>
    </div>
  );
}

export default Timeline;
